import React, { useEffect, useRef, useState } from 'react';
import './AgentVerificationBadges.scss';

export type VerificationType = 'email' | 'endpoint' | 'dns_txt';
export type VerificationStatus = 'pending' | 'verified' | 'stale';

export interface PublicVerification {
  id: string;
  type: VerificationType;
  targetValue: string;
  status: VerificationStatus;
  verifiedAt: string | null;
  staleSince?: string | null;
}

interface AgentVerificationBadgesProps {
  verifications: PublicVerification[];
  /** Extra class on the wrapper for spacing overrides at call
   *  sites (mirror of AgentCapabilityChips / AgentChainChips). */
  className?: string;
  size?: 'sm' | 'md';
}

/**
 * ADR-012 D4 public badge strip — one chip per verified/stale
 * verification row. Renders NOTHING for a pending-only agent
 * (pending is not a public trust signal per D6; the row exists
 * server-side but doesn't earn a chip until check completes).
 *
 * Grouping: chips are grouped by type so an agent with two verified
 * endpoints reads as a single "Endpoint · 2" chip rather than two
 * duplicated pills.
 *
 * Interaction: chips are <button> elements. Tapping / clicking a
 * chip toggles an inline details panel below the strip that lists
 * each target + its verifiedAt / staleSince timestamp. Same
 * behavior on desktop + mobile — deliberately not using the native
 * `title` attribute since that's desktop-hover-only and taps on
 * mobile do nothing. Click-outside + Escape close the panel.
 *
 * Color:
 * - verified → green fill (positive trust signal)
 * - stale    → amber fill (proved control historically, freshness
 *              slipped; still a signal but weaker)
 */
export const AgentVerificationBadges: React.FC<AgentVerificationBadgesProps> = ({
  verifications,
  className = '',
  size = 'md',
}) => {
  const displayable = (verifications ?? []).filter(
    (v) => v.status === 'verified' || v.status === 'stale',
  );

  const [openType, setOpenType] = useState<VerificationType | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click. Cheap listener attached only while
  // something is open — avoids the always-on cost + keeps the hook
  // dependency-array honest.
  useEffect(() => {
    if (openType === null) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpenType(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenType(null);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [openType]);

  if (displayable.length === 0) return null;

  // Group by type — same-type chips collapse into one badge with a
  // count. Preserves the ADR's "at-a-glance summary" intent without
  // exploding the chip strip when an agent verifies many endpoints.
  const groups = new Map<VerificationType, PublicVerification[]>();
  for (const v of displayable) {
    const existing = groups.get(v.type) ?? [];
    existing.push(v);
    groups.set(v.type, existing);
  }

  const openRows = openType ? groups.get(openType) ?? [] : [];

  return (
    <div
      ref={wrapperRef}
      className={`agent-verify-badges-wrapper agent-verify-badges-wrapper--${size} ${className}`.trim()}
    >
      <ul
        className="agent-verify-badges"
        aria-label="Agent verifications"
      >
        {Array.from(groups.entries()).map(([type, rows]) => {
          const allVerified = rows.every((r) => r.status === 'verified');
          const statusClass = allVerified ? 'verified' : 'stale';
          const isOpen = openType === type;
          return (
            <li key={type}>
              <button
                type="button"
                className={`agent-verify-badge agent-verify-badge--${type} agent-verify-badge--${statusClass}${isOpen ? ' is-open' : ''}`}
                aria-expanded={isOpen}
                aria-label={ariaLabelFor(type, rows, allVerified)}
                onClick={() => setOpenType(isOpen ? null : type)}
              >
                <span className="agent-verify-badge__icon" aria-hidden="true">
                  {iconFor(type)}
                </span>
                <span className="agent-verify-badge__label">{labelFor(type)}</span>
                {rows.length > 1 && (
                  <span className="agent-verify-badge__count">{rows.length}</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {openType && openRows.length > 0 && (
        <div
          className="agent-verify-badges__details"
          role="region"
          aria-label={`${labelFor(openType)} verification details`}
        >
          <div className="agent-verify-badges__details-title">
            {labelFor(openType)} verified
          </div>
          <ul className="agent-verify-badges__details-list">
            {openRows.map((r) => (
              <li key={r.id} className="agent-verify-badges__details-row">
                <span className="agent-verify-badges__details-target">
                  {r.targetValue}
                </span>
                <span className="agent-verify-badges__details-when">
                  {r.status === 'stale' && r.staleSince
                    ? `stale since ${new Date(r.staleSince).toLocaleDateString()}`
                    : r.verifiedAt
                      ? `verified ${new Date(r.verifiedAt).toLocaleDateString()}`
                      : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

function labelFor(type: VerificationType): string {
  switch (type) {
    case 'email':
      return 'Email';
    case 'endpoint':
      return 'Endpoint';
    case 'dns_txt':
      return 'DNS';
  }
}

function iconFor(type: VerificationType): string {
  switch (type) {
    case 'email':
      return '✉';
    case 'endpoint':
      return '🌐';
    case 'dns_txt':
      return '🔗';
  }
}

function ariaLabelFor(
  type: VerificationType,
  rows: PublicVerification[],
  allVerified: boolean,
): string {
  const stateWord = allVerified ? 'verified' : 'stale';
  return `${labelFor(type)} ${stateWord} (${rows.length}) — tap for details`;
}

export default AgentVerificationBadges;
